/**
 * MeasurementsPage Component
 *
 * Main page for body measurements tracking.
 * Displays measurement entry form, history list, and trend visualizations.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MeasurementEntryForm from '../components/measurements/MeasurementEntryForm';
import MeasurementHistoryList from '../components/measurements/MeasurementHistoryList';
import MeasurementTrendChart from '../components/measurements/MeasurementTrendChart';
import UnitToggle, { UnitSystem } from '../components/measurements/UnitToggle';
import { apiService } from '../services/api';
import type { ListMeasurementsResponse, TrendsResponse } from '../types/measurements';
import { useToast } from '../components/Toast';

const MeasurementsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const [selectedDays] = useState<number>(30);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('metric');

  // Fetch measurement history
  const {
    data: historyData,
    isLoading: historyLoading,
  } = useQuery<ListMeasurementsResponse>({
    queryKey: ['measurements', 'list', selectedDays],
    queryFn: async () => {
      return apiService.get(`/measurements?days=${selectedDays}&limit=30`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch trend data
  const {
    data: trendsData,
    isLoading: trendsLoading,
  } = useQuery<TrendsResponse>({
    queryKey: ['measurements', 'trends', selectedDays],
    queryFn: async () => {
      return apiService.get(`/measurements/trends?days=${selectedDays}`);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const measurements = historyData?.measurements || [];
  const hasData = measurements.length > 0;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiService.delete(`/measurements/${id}`);
    },
    onSuccess: () => {
      success('Measurement deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
    },
    onError: () => {
      showError('Failed to delete measurement');
    }
  });

  // Handlers
  const handleEdit = () => {
    // Scroll to form - it will auto-load today's measurement
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-serif font-bold text-primary-700 mb-2">
            Body Measurements
          </h1>
          <p className="text-neutral-600">
            Track your body measurements over time to monitor your wellness journey.
          </p>
        </div>
        <UnitToggle
          currentUnit={unitSystem}
          onToggle={setUnitSystem}
          className="flex-shrink-0"
        />
      </div>

      {/* Measurement Entry Form */}
      <MeasurementEntryForm />

      {/* Measurement History List */}
      {(hasData || historyLoading) && (
        <MeasurementHistoryList
          measurements={measurements}
          isLoading={historyLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          displayUnit={unitSystem}
        />
      )}

      {/* Measurement Trend Chart */}
      {(hasData || trendsLoading) && (
        <MeasurementTrendChart
          trendsData={trendsData}
          isLoading={trendsLoading}
          displayUnit={unitSystem}
        />
      )}

      {/* Empty State - No measurements at all */}
      {!hasData && !historyLoading && !trendsLoading && (
        <div className="bg-white rounded-lg shadow-soft p-12 text-center">
          <div className="text-6xl mb-4">📏</div>
          <h3 className="text-2xl font-serif font-bold text-neutral-700 mb-2">
            Start Tracking Your Progress
          </h3>
          <p className="text-neutral-600 mb-6">
            Record your first measurement above to begin tracking your body composition changes over time.
          </p>
          <div className="flex justify-center gap-8 text-sm text-neutral-500">
            <div>
              <div className="font-semibold text-primary-700 text-2xl">5</div>
              <div>Required Fields</div>
            </div>
            <div>
              <div className="font-semibold text-primary-700 text-2xl">30s</div>
              <div>Average Time</div>
            </div>
            <div>
              <div className="font-semibold text-primary-700 text-2xl">∞</div>
              <div>History Stored</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeasurementsPage;
